import React, { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import { Tooltip } from '../Tooltip';

/* ===============================
   TYPES
   =============================== */

export interface ActionMenuItemProps {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: 'default' | 'destructive';
  icon?: React.ReactNode;
}

export interface ActionMenuProps {
  children: React.ReactNode;
}

/* ===============================
   ACTIONMENU (Container)
   =============================== */

interface MenuPosition {
  top: number;
  left: number;
  openUpward: boolean;
  visible: boolean;
}

const ActionMenuBase: React.FC<ActionMenuProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<MenuPosition>({ top: -9999, left: -9999, openUpward: false, visible: false });
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Tras renderizar el portal, mide la altura real del menú y reposiciona
  const adjustPosition = useCallback(() => {
    if (!triggerRef.current || !menuRef.current) return;
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const menuHeight = menuRef.current.offsetHeight;
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const openUpward = spaceBelow < menuHeight + 8;

    setMenuPos({
      top: openUpward ? triggerRect.top - menuHeight - 4 : triggerRect.bottom + 4,
      left: triggerRect.right - menuRef.current.offsetWidth,
      openUpward,
      visible: true,
    });
  }, []);

  const handleOpen = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    // Posición provisional fuera de pantalla para renderizar y medir
    setMenuPos({ top: -9999, left: rect.right - 192, openUpward: false, visible: false });
    setIsOpen(prev => !prev);
  }, []);

  // Se ejecuta síncronamente tras el render del portal para medir y reposicionar sin parpadeo
  useLayoutEffect(() => {
    if (isOpen) adjustPosition();
  }, [isOpen, adjustPosition]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        menuRef.current && !menuRef.current.contains(target) &&
        triggerRef.current && !triggerRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => setIsOpen(false);

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  return (
    <div ref={triggerRef} className="relative inline-block text-left">
      {React.Children.map(children, child => {
        if (React.isValidElement(child)) {
          if (child.type === ActionMenuTrigger) {
            return React.cloneElement(child as React.ReactElement<Record<string, unknown>>, {
              onClick: handleOpen,
              isOpen,
            });
          }
          if (child.type === ActionMenuContent && isOpen) {
            return ReactDOM.createPortal(
              <div
                ref={menuRef}
                style={{
                  position: 'fixed',
                  top: menuPos.top,
                  left: menuPos.left,
                  zIndex: 9999,
                  visibility: menuPos.visible ? 'visible' : 'hidden',
                }}
              >
                {React.cloneElement(child as React.ReactElement<Record<string, unknown>>, {
                  onClose: () => setIsOpen(false),
                  openUpward: menuPos.openUpward,
                })}
              </div>,
              document.body
            );
          }
        }
        return null;
      })}
    </div>
  );
};

/* ===============================
   ACTIONMENU.TRIGGER
   =============================== */

export interface ActionMenuTriggerProps {
  children: React.ReactNode;
  onClick?: () => void;
  tooltip?: string;
  passthrough?: boolean;
  isOpen?: boolean;
}

export const ActionMenuTrigger: React.FC<ActionMenuTriggerProps> = ({ children, onClick, tooltip, passthrough, isOpen }) => {
  if (passthrough && React.isValidElement(children)) {
    const child = React.cloneElement(children as React.ReactElement<Record<string, unknown>>, { onClick, isOpen });
    return tooltip ? <Tooltip text={tooltip} position="left">{child}</Tooltip> : <>{child}</>;
  }

  const button = (
    <button type="button"
      onClick={onClick}
      className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-surface-1 transition-colors text-tertiary hover:text-secondary"
    >
      {children}
    </button>
  );

  if (tooltip) {
    return <Tooltip text={tooltip} position="left">{button}</Tooltip>;
  }

  return button;
};

ActionMenuTrigger.displayName = 'ActionMenuTrigger';

/* ===============================
   ACTIONMENU.CONTENT
   =============================== */

export interface ActionMenuContentProps {
  children: React.ReactNode;
  onClose?: () => void;
  openUpward?: boolean;
}

export const ActionMenuContent: React.FC<ActionMenuContentProps> = ({ children, onClose, openUpward }) => (
  <div
    className={`w-48 rounded-lg bg-surface-0 shadow-lg ring-1 ring-black/5 dark:ring-white/10 ${
      openUpward ? 'origin-bottom-right' : 'origin-top-right'
    }`}
  >
    <div className="py-1" onClick={onClose}>
      {children}
    </div>
  </div>
);

ActionMenuContent.displayName = 'ActionMenuContent';

/* ===============================
   ACTIONMENU.ITEM
   =============================== */

export const ActionMenuItem: React.FC<ActionMenuItemProps> = ({
  children,
  onClick,
  variant = 'default',
  icon
}) => {
  const variantClasses = {
    default: 'text-secondary hover:bg-surface-hover',
    destructive: 'text-error hover:bg-error-bg'
  };

  return (
    <button type="button"
      onClick={onClick}
      className={`w-full text-left px-4 py-2 text-ui-sm flex items-center gap-2 transition-colors ${variantClasses[variant]}`}
    >
      {icon && <span className="w-4 h-4 flex-shrink-0">{icon}</span>}
      {children}
    </button>
  );
};

ActionMenuItem.displayName = 'ActionMenuItem';

/* ===============================
   ACTIONMENU.SEPARATOR
   =============================== */

export const ActionMenuSeparator: React.FC = () => (
  <div className="my-1 h-px bg-border-muted" />
);

ActionMenuSeparator.displayName = 'ActionMenuSeparator';

/* ===============================
   COMPOUND COMPONENT SETUP
   =============================== */

export const ActionMenu = Object.assign(ActionMenuBase, {
  Trigger: ActionMenuTrigger,
  Content: ActionMenuContent,
  Item: ActionMenuItem,
  Separator: ActionMenuSeparator,
});



